import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Search, ChevronDown, ChevronUp } from "lucide-react";
import { Playbook, PlaybookShort } from "../model/Playbook";
import { useSearchPlaybooks } from "../api/useSearchPlaybooks";
import { PlaybookCardv2 } from "./PlaybookCardv2";
import { useDebouncedValue } from "@mantine/hooks";
import { PlaybookType } from "../models/PlaybookType";

interface PlaybookSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (
    playbook: Playbook | PlaybookShort,
    isSystem: boolean,
  ) => Promise<void>;
  loadingPlaybookId: string | null;
}

export function PlaybookSelectionModal({
  open,
  onClose,
  onSelect,
  loadingPlaybookId,
}: PlaybookSelectionModalProps) {
  const { playbooks, searchPlaybooks } = useSearchPlaybooks();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300);
  const [showAllSystemPlaybooks, setShowAllSystemPlaybooks] = useState(false);
  const [showAllUserPlaybooks, setShowAllUserPlaybooks] = useState(false);
  const [userPlaybooks, setUserPlaybooks] = useState<PlaybookShort[]>([]);
  const [systemPlaybooks, setSystemPlaybooks] = useState<PlaybookShort[]>([]);

  useEffect(() => {
    if (open) {
      searchPlaybooks({ query: debouncedSearchQuery });
    } else {
      // Reset all states when the main modal is closed
      setSearchQuery("");
      setShowAllSystemPlaybooks(false);
      setShowAllUserPlaybooks(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debouncedSearchQuery]);

  useEffect(() => {
    if (!playbooks) return;
    setUserPlaybooks(
      playbooks.filter((playbook) => playbook.type === PlaybookType.User),
    );
    setSystemPlaybooks(
      playbooks.filter((playbook) => playbook.type === PlaybookType.System),
    );
  }, [playbooks]);

  // Show "Show All" button if there are more than 3 playbooks
  const hasMoreSystemPlaybooks = systemPlaybooks.length > 3;
  const hasMoreUserPlaybooks = useDebouncedValue.length > 3;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[70rem] w-[90vw] max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle>Select a Playbook</DialogTitle>
          <DialogDescription>
            Choose from our default playbooks or create your own custom
            playbook.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex flex-col">
          {/* Search Bar */}
          <div className="px-6 py-4 border-b bg-white shrink-0">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search playbooks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="px-6 py-4 space-y-6">
              {/* Default Playbooks Section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Default Playbooks</h3>
                  {hasMoreSystemPlaybooks && (
                    <Button
                      variant="ghost"
                      className="gap-2 text-muted-foreground"
                      onClick={() =>
                        setShowAllSystemPlaybooks(!showAllSystemPlaybooks)
                      }
                    >
                      {showAllSystemPlaybooks ? (
                        <>
                          Show Less
                          <ChevronUp className="size-4" />
                        </>
                      ) : (
                        <>
                          Show All ({systemPlaybooks.length})
                          <ChevronDown className="size-4" />
                        </>
                      )}
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(showAllSystemPlaybooks
                    ? systemPlaybooks
                    : systemPlaybooks.slice(0, 3)
                  ).map((playbook) => (
                    <PlaybookCardv2
                      key={playbook.id}
                      playbook={playbook}
                      setActivePlaybook={(p) => p && onSelect(p, true)}
                      onDelete={() => {}}
                      descriptionCutoff={250}
                      isDisabled={loadingPlaybookId !== null}
                      isButtonLoading={loadingPlaybookId === playbook.id}
                    />
                  ))}
                </div>
              </div>

              {/* Custom Playbooks Section */}
              {playbooks && playbooks.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Your Playbooks</h3>
                    {hasMoreUserPlaybooks && (
                      <Button
                        variant="ghost"
                        className="gap-2 text-muted-foreground"
                        onClick={() =>
                          setShowAllUserPlaybooks(!showAllUserPlaybooks)
                        }
                      >
                        {showAllUserPlaybooks ? (
                          <>
                            Show Less
                            <ChevronUp className="size-4" />
                          </>
                        ) : (
                          <>
                            Show All ({userPlaybooks.length})
                            <ChevronDown className="size-4" />
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(showAllUserPlaybooks
                      ? userPlaybooks
                      : userPlaybooks.slice(0, 3)
                    ).map((playbook) => (
                      <PlaybookCardv2
                        key={playbook.id}
                        playbook={playbook}
                        setActivePlaybook={(p) => onSelect(p, false)}
                        onDelete={() => {
                          searchPlaybooks({ query: debouncedSearchQuery });
                        }}
                        descriptionCutoff={250}
                        isDisabled={loadingPlaybookId !== null}
                        isButtonLoading={loadingPlaybookId === playbook.id}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
